import { fromEvent, Subject, Subscription, BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ExponentialBackoffPolicy, IReconnectPolicy } from './policy';
import { environment } from 'src/environments/environment';

// ==================== 類型定義 ====================

export interface GatewayMessage {
	type: string;
	id?: string;
	name?: string;
	data?: any;
	error?: any;
}

export interface RequestOptions {
	policy?: IReconnectPolicy;
	timeout?: number;  // 保留 timeout 用於請求超時
}

export interface ConnectionStrategy {
	timeout: number;
	maxRetries: number;
	retryInterval: number;
	description: string;
}

export interface WebSocketChannelOptions {
	ws?:string;
	url?: string;
	reconnectInterval?: number;
	maxRetries?: number;
	policy?: IReconnectPolicy;
	name?: string;
}

interface Resolver {
	resolve: (data: any) => void;
	reject: (reason: any) => void;
	completed?: boolean;
}

export interface GatewayChannel {
	send(data: any): { success: boolean; error?: any } | void;
	subscribe(handler: (value: any) => void): Subscription;
	close(): void;
	connect?(): Promise<void>;
	isConnected?(): boolean;
	connectionStrategy?: ConnectionStrategy;
}

// ==================== PromiseUtils ====================

export class PromiseUtils {
	static sleep(time: number): Promise<void> {
		return new Promise((resolve) => window.setTimeout(resolve, time));
	}
}

// ==================== WebSocketChannel ====================

export class WebSocketChannel implements GatewayChannel {
	protected ws: WebSocket | null;
	protected url!: string;
	protected policy: IReconnectPolicy;
	protected retryCount: number;
	protected name?: string;
	protected subject: Subject<any>;
	protected reconnectInterval: number;
	protected maxRetries: number;
	protected status!: 'idle' | 'connecting' | 'connected' | 'closed';
	protected reconnectTimer: number | null;
	protected subscription: Subscription | null;
	protected options: WebSocketChannelOptions;

	// Connection strategy for gateway
	public connectionStrategy: ConnectionStrategy = {
		timeout: 1000,
		maxRetries: 30,
		retryInterval: 300,
		description: "WebSocket - wait for AS3 connection"
	};

	constructor(options: WebSocketChannelOptions = {}) {
		this.options = options;
		this.url = options.url || '';
		this.policy = options.policy || new ExponentialBackoffPolicy(1000, 15000, 999999);
		this.name = options.name || 'WebSocketChannel';
		this.retryCount = 0;
		this.subject = new Subject<any>();
		this.reconnectInterval = options.reconnectInterval || 1000;
		this.maxRetries = options.maxRetries || 999999;
		this.status = 'idle';
		this.reconnectTimer = null;
		this.subscription = null;
		this.ws = null;
	}

	isConnected(): boolean {
		return this.status === 'connected';
	}

	async connect(): Promise<void> {
		if (this.status === 'connecting' || this.status === 'connected') {
			console.warn(`[${this.name}] already connecting/connected, skip`);
			return;
		}

		this.cleanUp();
		this.subscription = new Subscription();
		const url = this.options.url;
		
		if (!url) {
			throw new Error('WebSocket URL is required');
		}

		console.log(`[${this.name}] connecting`, url);
		this.status = "connecting";

		return new Promise((resolve, reject) => {
			this.ws = new WebSocket(url);
			const ws = this.ws;
			const timeoutId = setTimeout(() => {
				if (this.status === 'connecting') {
					this.ws?.close();
					reject(new Error('WebSocket connection timeout'));
				}
			}, 8000);

			this.ws.onopen = () => {
				clearTimeout(timeoutId);
				this.status = "connected";
				console.log(`[${this.name}] Connected:`, url);
				this.retryCount = 0;
				resolve();
			};

			this.ws.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data);
					console.log(`[${this.name}] on message`, data);
					if (data.type === "ping") {
						ws.send(JSON.stringify({ type: "pong", timestamp: Date.now() }));
					} else {
						this.subject.next(data);
					}
				} catch (e) {
					console.log(`[${this.name}] Invalid JSON:`, event.data);
				}
			};

			this.ws.onerror = (err) => {
				console.error(`[${this.name}] WebSocket error:`, err);
			};

			this.ws.onclose = () => {
				const wasConnecting = this.status === 'connecting';
				clearTimeout(timeoutId);
				this.status = "closed";
				console.log(`[${this.name}] Disconnected:`, url);
				
				if (wasConnecting) {
					reject(new Error('WebSocket connection closed'));
				}
				this.scheduleReconnect();
			};

			this.subscription?.add(() => {
				if (this.ws) {
					this.ws.onopen = null;
					this.ws.onmessage = null;
					this.ws.onerror = null;
					this.ws.onclose = null;
					if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
						this.ws.close();
					}
					this.ws = null;
				}
			});
		});
	}

	protected scheduleReconnect(): void {
		if (this.retryCount >= this.maxRetries) {
			console.log(`[${this.name}] Max retries reached, giving up.`);
			this.subject.error(new Error('Max retries exceeded'));
			return;
		}

		if (this.status !== 'closed') return;

		this.retryCount++;
		const delay = Math.min(this.reconnectInterval * this.retryCount, 15000);
		console.log(`[${this.name}] Reconnecting in ${delay}ms... (attempt ${this.retryCount})`);

		if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
		this.reconnectTimer = window.setTimeout(async () => {
			if (this.status === 'closed') {
				this.cleanupWebSocket();
				try {
					await this.connect();
				} catch (err) {
					console.warn(`[${this.name}] Reconnection failed`, err);
				}
			}
		}, delay);
	}

	protected cleanupWebSocket(): void {
		if (this.ws) {
			try {
				this.ws.onopen = null;
				this.ws.onmessage = null;
				this.ws.onerror = null;
				this.ws.onclose = null;
				if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
					this.ws.close();
				}
			} catch (e) {}
			this.ws = null;
		}
	}

	protected cleanUp(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	close(): void {
		this.status = 'idle';
		this.cleanUp();
		this.cleanupWebSocket();
		if (this.subject && !this.subject.closed) {
			this.subject.complete();
		}
		this.subject = new Subject<any>();
	}

	send(obj: any): { success: boolean; error?: any } {
		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(obj));
			return { success: true };
		} else {
			console.log(`[${this.name}] Cannot send, socket not open.`);
			return {
				success: false,
				error: {
					type: "no_connection",
					message: "WebSocket not open",
					status: this.status
				}
			};
		}
	}

	subscribe(handler: (value: any) => void): Subscription {
		return this.subject.subscribe(handler);
	}
}

// ==================== AutoWebSocketChannel ====================

export class AutoWebSocketChannel extends WebSocketChannel {
	protected channelID: string;
	protected titleInterval: number | null;

	constructor(options: WebSocketChannelOptions = {}) {
		super(options);
		this.channelID = this.createChannelID();
		this.titleInterval = null;

		// Override connection strategy for AutoWebSocket
		this.connectionStrategy = {
			timeout: 1000,
			maxRetries: 30,
			retryInterval: 300,
			description: "AutoWebSocket - wait for AS3 connection"
		};
	}

	async connect(): Promise<void> {
		// var wsURL = `ws://localhost:8080/connect/${this.channelID}/client`;
		var wsURL = this.options.ws ? `${this.options.ws}/connect/${this.channelID}/host` :`${environment.wsGateway}/connect/${this.channelID}/host`;
		this.options.url = wsURL;
		this.updateChannelIDInTitle();
		if (this.titleInterval) clearInterval(this.titleInterval);
		this.titleInterval = window.setInterval(() => this.updateChannelIDInTitle(), 500);
		await super.connect();
		await PromiseUtils.sleep(1500);
	}

	close(): void {
		if (this.titleInterval) {
			clearInterval(this.titleInterval);
			this.titleInterval = null;
		}
		super.close();
		// document.title = document.title.replace(/\s*#ro\/channel\/[a-z0-9\-]+/i, '');
	}

	protected cleanUp(): void {
		super.cleanUp();
		if (this.titleInterval) {
			clearInterval(this.titleInterval);
			this.titleInterval = null;
		}
	}

	protected updateChannelIDInTitle(): void {
		var title = document.title.replace(/#.*/g, "");
		document.title = title + "#ro/channel/" + this.channelID;
	}

	protected createChannelID(): string {
		return "channel-" + Date.now().toString(36) + "-" + Math.random().toString(36).substring(2, 8);// substr(2, 6);
	}
}

// ==================== AIRWebViewChannel ====================

export class AIRWebViewChannel implements GatewayChannel {
	protected subject: Subject<any>;
	protected subscription: Subscription | null;
	protected boundWatchHandler: (event: MessageEvent) => void;

	// Connection strategy for gateway
	public connectionStrategy: ConnectionStrategy = {
		timeout: 2000,
		maxRetries: 1,
		retryInterval: 0,
		description: "AIR channel - immediate connection"
	};

	constructor(options: any = {}) {
		this.subject = new Subject<any>();
		this.subscription = null;
		this.boundWatchHandler = this.watchAIRMessage.bind(this);
	}

	isConnected(): boolean {
		const w = window as any;
		return !!(w.AIR || (w.webkit?.messageHandlers?.as3) || (w.chrome?.webview));
	}

	async connect(): Promise<void> {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}

		const w = window as any;
		const target = w.chrome?.webview ? w.chrome.webview : w;
		this.subscription = fromEvent<MessageEvent>(target, 'message').subscribe(this.boundWatchHandler);
		console.log('[AIRWebViewChannel] bridge listening');
	}

	close(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
		if (this.subject && !this.subject.closed) {
			this.subject.complete();
		}
	}


	lastNonEmptyElement(array: any[]) {
		for (let i = array.length - 1; i >= 0; i--) {
			let el = array[i];
			if (Array.isArray(el)) {
				if (el.length > 0) {
					// Return the last element inside the nested array
					return el[el.length - 1];
				}
			} else if (el !== null && el !== undefined && el !== "") {
				return el;
			}
		}
		return null;
	}

	base64Encode(str: string): string {
		const bytes = new TextEncoder().encode(str);
		let binary = "";
		bytes.forEach(b => binary += String.fromCharCode(b));
		return btoa(binary);
	}

	base64Decode(str: string): string {
		const bytes = Uint8Array.from(atob(str), c => c.charCodeAt(0));
		return new TextDecoder("utf-8").decode(bytes);
	}

	decodeBase64Utf8(str: string): string {
		const bytes = Uint8Array.from(atob(str), c => c.charCodeAt(0));
		return new TextDecoder("utf-8").decode(bytes);
	}
	processMessage(txt: string) {
		try {
			if (!txt) return;
			if (txt && /[\[\{]/.test(txt) == false)
				txt = this.base64Decode(txt);
			const json = JSON.parse(txt);
			var message = null;
			if (json instanceof Array) {
				message = this.lastNonEmptyElement(json);
			} else {
				message = json;
			}
			return message;
		} catch (reason) {
			console.error(reason);
		}
		return null;
	}

	protected watchAIRMessage(event: MessageEvent): void {
		if (typeof event.data === 'string') {
			try {
				var message = this.processMessage(event.data);
				if (message && message.type === 'response' && message.id) {
					this.subject.next(message);
				}
			} catch (reason) {
				console.error(reason);
			}
		}
	}

	send(data: any): { success: boolean; error?: any } {
		console.log(this.constructor.name, "send", data);
		const w = window as any;
		const message = JSON.stringify(data);
		
		if (w.AIR) {
			w.AIR.postMessage(message);
			return { success: true };
		} else if (w.webkit?.messageHandlers?.as3) {
			w.webkit.messageHandlers.as3.postMessage(message);
			return { success: true };
		} else if (w.chrome?.webview) {
			w.chrome.webview.postMessage(message);
			return { success: true };
		} else {
			return {
				success: false,
				error: {
					type: "no_connection",
					message: "AIR Channel Not Support"
				}
			};
		}
	}

	subscribe(handler: (value: any) => void): Subscription {
		return this.subject.subscribe(handler);
	}
}

// ==================== WebViewGateway ====================

export class WebViewGateway {
	protected ready!: boolean;
	protected map!: Record<string, Resolver>;
	protected prefix!: string;
	protected messageIndex!: number;
	protected channels!: GatewayChannel[];
	protected timeoutMap!: Record<string, number>;
	protected systemReadySubject!: BehaviorSubject<boolean>;
	protected systemReady$!: any; // Observable<boolean>
	protected defaultRetryPolicy: ExponentialBackoffPolicy;

	constructor() {
		this.init();
		// Default retry policy: Exponential backoff (1s, 2s, 4s, 8s, max 15s)
		this.defaultRetryPolicy = new ExponentialBackoffPolicy(1000, 15000, 7);
	}

	protected init(): void {
		this.ready = false;
		this.map = {};
		this.prefix = 'random-';
		this.messageIndex = 0;
		this.channels = [];
		this.timeoutMap = {};

		this.systemReadySubject = new BehaviorSubject<boolean>(false);
		this.systemReady$ = this.systemReadySubject.asObservable();
	}

	public dispose(): void {
		this.channels.forEach((channel: GatewayChannel) => {
			if (channel) channel.close();
		});
		this.channels = [];
		this.map = {};
	}

	addChannel(channel: GatewayChannel): void {
		if (!this.channels.includes(channel)) {
			this.channels.push(channel);
			channel.subscribe(this.onMessage.bind(this));
		}
	}

	removeChannel(channel: GatewayChannel): void {
		const index = this.channels.indexOf(channel);
		if (index !== -1) this.channels.splice(index, 1);
	}

	protected prepare(id: string): Promise<any> {
		return new Promise((resolve, reject) => {
			this.addResolver(id, { resolve, reject });
		});
	}

	protected addResolver(id: string, resolver: Resolver): void {
		this.map[id] = resolver;
	}

	protected createMessage(name: string, data: any): GatewayMessage {
		this.messageIndex++;
		const id = this.prefix + this.messageIndex;
		return { type: 'request', id, name, data };
	}

	systemIsReady(): void {
		console.log('[Gateway] System is ready');
		this.ready = true;
		this.systemReadySubject.next(true);
	}

	systemIsNotReady(): void {
		this.ready = false;
		this.systemReadySubject.next(false);
	}

	// 使用 ExponentialBackoffPolicy 的 retry 方法
	requestWithPolicy(name: string, data: any, options: RequestOptions = {}, skipReadyCheck: boolean = false): Promise<any> {
		const retryPolicy = options.policy || this.defaultRetryPolicy;
		const maxRetries = retryPolicy.getMaxRetries();
		const timeout = options.timeout;
		
		console.log(`[Gateway] Using retry policy: ${retryPolicy.getPolicyName()}`);
		
		const attempt = async (retryCount: number): Promise<any> => {
			try {
				return await this._request(name, data, { timeout }, skipReadyCheck);
			} catch (err: any) {
				const retryableErrors = ["not_ready", 'no_connection', "no_peer"];
				const shouldRetry = retryableErrors.indexOf(err?.type) !== -1;
				
				if (shouldRetry && retryCount < maxRetries) {
					const delay = retryPolicy.getDelay(retryCount);
					console.log(`[Gateway] Retry ${retryCount + 1}/${maxRetries} for "${name}" in ${delay}ms (${retryPolicy.getPolicyName()})`);
					
					if (!skipReadyCheck && err?.type === "not_ready") {
						await this.systemReady$.pipe(
							filter((ready: boolean) => ready === true),
							take(1)
						).toPromise();
					} else {
						await PromiseUtils.sleep(delay);
					}
					
					return attempt(retryCount + 1);
				}
				throw err;
			}
		};
		
		return attempt(0);
	}

	// 主要請求方法
	request(name: string, data: any, options: RequestOptions = {}, skipReadyCheck: boolean = false): Promise<any> {
		return this.requestWithPolicy(name, data, options, skipReadyCheck);
	}

	protected async _request(name: string, data: any, options: { timeout?: number } = {}, skipReadyCheck: boolean = false): Promise<any> {
		console.log(this.constructor.name, 'request', name, data);

		if (this.channels.length <= 0) {
			return Promise.reject({ type: "no_connection", message: "no channel is added" });
		}

		const msg = this.createMessage(name, data);
		const promise = this.prepare(msg.id!);

		try {
			this.send(msg, skipReadyCheck);
		} catch (reason) {
			return Promise.reject(reason);
		}

		if (options.timeout) {
			const timerId = window.setTimeout(() => {
				this.reject(msg.id!, { type: "timeout", message: `Request timeout after ${options.timeout}ms` });
			}, options.timeout);
			this.timeoutMap[msg.id!] = timerId;
			promise.finally(() => {
				if (this.timeoutMap[msg.id!]) {
					clearTimeout(this.timeoutMap[msg.id!]);
					delete this.timeoutMap[msg.id!];
				}
			});
		}

		return promise;
	}

	async pickConnection(channels: GatewayChannel[], timeout: number = 2000): Promise<GatewayChannel | null> {
		this.systemIsNotReady();
		
		for (let i = 0; i < channels.length; i++) {
			const channel = channels[i];
			try {
				const strategy = channel.connectionStrategy || {
					timeout: 2000,
					maxRetries: 3,
					retryInterval: 1000,
					description: "default"
				};
				
				console.log("add channel", channel.constructor.name);
				this.addChannel(channel);
				console.log(channel.constructor.name, "connect");
				if (channel.connect) {
					await channel.connect();
				}
				
				// 使用 policy retry，maxRetries=1（只試一次）
				const data = await this.requestWithPolicy(
					"supportAIRMessage",
					null,
					{ 
						timeout: strategy.timeout,
						policy: new ExponentialBackoffPolicy(1000, 15000, strategy.maxRetries)  // 只試一次
					},
					true  // skipReadyCheck
				);
				
				console.log(channel.constructor.name, "supportAIRMessage success", data);
				this.systemIsReady();
				return channel;
			} catch (reason) {
				console.log("pick connection", channel.constructor.name, "failed", reason);
			}
			this.removeChannel(channel);
			if (channel.close) {
				channel.close();
			}
		}
		this.systemIsNotReady();
		return null;
	}

	protected resolve(id: string, data: any): void {
		if (this.timeoutMap[id]) {
			clearTimeout(this.timeoutMap[id]);
			delete this.timeoutMap[id];
		}
		const resolver = this.map[id];
		if (resolver && !resolver.completed) {
			console.log("resolve", id, data);
			resolver.resolve(data);
			resolver.completed = true;
		}
		delete this.map[id];
	}

	protected reject(id: string, reason: any): void {
		if (this.timeoutMap[id]) {
			clearTimeout(this.timeoutMap[id]);
			delete this.timeoutMap[id];
		}
		const resolver = this.map[id];
		if (resolver && !resolver.completed) {
			console.log("reject", id, reason);
			resolver.reject(reason);
			resolver.completed = true;
		}
		delete this.map[id];
	}

	protected send(data: GatewayMessage, skipReadyCheck: boolean = false): void {
		if (!skipReadyCheck && !this.ready) {
			throw { type: "not_ready", message: "System not ready" };
		}
		console.log(this.constructor.name, "send", data);
		let anySuccess = false;
		const errors: any[] = [];
		for (const channel of this.channels) {
			const result = channel.send(data);
			if (result && 'success' in result) {
				if (result.success) {
					anySuccess = true;
				} else if (result.error) {
					errors.push(result.error);
				}
			}
		}
		if (!anySuccess && errors.length > 0) {
			throw errors[0];
		}
	}

	protected onMessage(message: GatewayMessage): void {
		console.log("onMessage", message);
		if (message.type === 'response' && message.id) {
			const resolver = this.map[message.id];
			if (resolver && !resolver.completed) {
				message.error ? resolver.reject(message.error) : resolver.resolve(message.data);
				resolver.completed = true;
			}
			delete this.map[message.id];
		}
	}
}

// ==================== WebSocketGateway (alias for backward compatibility) ====================

export class WebSocketGateway extends WebViewGateway {
	constructor() {
		super();
	}
}